// Operator-facing outcome of a campaignRunId-scoped Ads view.
//
// The backend already writes a human sentence onto a terminal empty run
// (CampaignRun.errors[].message via summarizeEmptyExpansion, and/or
// notice.message). This module only decides WHEN to show it vs. the
// "waiting for the first creative to land" copy — it does not paraphrase.
//
// Three outcomes the operator actually cares about. Conflating them is
// the bug this exists to close (measured on run_1787578615444_d2cdcb3e:
// status=done, total=0, errors[0].message set, Ads page still said
// "they will appear here as soon as the first one lands"):
//   waiting                — still working; keep polling, keep the
//                            waiting copy
//   finished-empty         — run is terminal and this batch has 0 ads;
//                            show the backend's sentence
//   finished-with-results  — run is terminal (or ads have already
//                            landed) and there is something to show
//
// IN-PROGRESS GUARD — do not treat total===0 as terminal. A `preparing`
// run always has total 0 (expand+claim have not run yet). Calling that
// "finished with nothing" would look like a dead run and invite a
// second Generate click, which on a static selection can bill. Only
// `status === 'done' | 'failed'` is terminal. A missing run (first poll
// not back) is waiting, never finished.

export type RunOutcomeStatus = 'preparing' | 'running' | 'done' | 'failed';

export type RunOutcomeNotice = { code?: string; message?: string } | null | undefined;

export type RunOutcomeError = {
  index?: number;
  stage?: string;
  template?: string;
  aspectRatio?: string;
  message?: string;
};

export type RunOutcomeInput = {
  status: RunOutcomeStatus;
  total?: number;
  errors?: RunOutcomeError[] | null;
  notice?: RunOutcomeNotice;
} | null;

export type LatestBatchOutcome = 'waiting' | 'finished-empty' | 'finished-with-results';

export const LATEST_BATCH_WAITING_COPY =
  'No ads in this batch have finished rendering yet — they will appear here as soon as the first one lands. The status above keeps updating.';

export const LATEST_BATCH_EMPTY_FALLBACK =
  'This run finished without producing any new creatives.';

export function isRunTerminal(status: RunOutcomeStatus | null | undefined): boolean {
  return status === 'done' || status === 'failed';
}

// Dual of isRunTerminal for the Ads-page poller. CampaignRun.status is a
// closed mongoose enum: preparing | running | done | failed
// (liquidretail_backend/models/CampaignRun.js). Every member is classified:
//   preparing, running → waiting, keep polling
//   done, failed       → terminal, stop polling
// run=null (first poll not back) is waiting; the poller is already in
// flight and does not consult this helper until a fetch returns.
// There is no leftover status today. If the backend adds one,
// classifyLatestBatch falls through to waiting (safe: never call a live
// run finished). Pin it in the harness the same day and decide whether
// this helper includes it (default yes — an unhandled status that stops
// the poller is how a live run looks dead).
export function shouldKeepPolling(status: RunOutcomeStatus | null | undefined): boolean {
  return status === 'preparing' || status === 'running';
}

export function classifyLatestBatch(opts: {
  run: RunOutcomeInput;
  rowCount: number;
}): LatestBatchOutcome {
  const { run, rowCount } = opts;
  if (rowCount > 0) return 'finished-with-results';
  if (run && isRunTerminal(run.status)) {
    // total>0 means the run DID mint/claim ads. 0 visible rows is a
    // view issue (status filter, or the ads fetch still in flight on
    // the tick that flipped status to done) — not "produced nothing".
    // Calling that finished-empty painted "This run finished without
    // producing any new creatives" next to "Run complete — 12 rendered"
    // and would invite a second Generate.
    if ((run.total ?? 0) > 0) return 'finished-with-results';
    return 'finished-empty';
  }
  // preparing / running / first poll not back — including total===0.
  return 'waiting';
}

function firstLine(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const line = raw.trim().split(/\n/)[0] || '';
  return line.trim();
}

// One operator sentence. Never a raw object, never a stack.
//
// errors[].message FIRST: on an expand-empty run that is the
// summarizeEmptyExpansion sentence for THIS run. notice.message can be
// a mint-time overlap stamp (`concurrent-run-shares-products`) that is
// NOT cleared when expand then collides — RunProgress already treats
// that notice as stale on a settled run, and preferring it here would
// show "both will run and both will be billed" for a run that minted
// nothing. minted-ads-unclaimed still surfaces via notice when there
// is no errors[] sentence (and typically total>0, so this empty-state
// path does not apply).
// perProduct[] is deliberately not rendered: the run-level message
// already collapsed those reasons.
export function runTerminalExplanation(run: RunOutcomeInput): string | null {
  if (!run) return null;
  const errors = Array.isArray(run.errors) ? run.errors : [];
  for (const entry of errors) {
    if (!entry) continue;
    const msg = firstLine(entry.message);
    if (msg) return msg;
  }
  const noticeCode = run.notice && typeof run.notice.code === 'string' ? run.notice.code : '';
  const staleOverlap =
    isRunTerminal(run.status) && noticeCode === 'concurrent-run-shares-products';
  if (staleOverlap) return null;
  const notice = firstLine(run.notice?.message);
  return notice || null;
}

export function latestBatchEmptyCopy(run: RunOutcomeInput): string {
  const outcome = classifyLatestBatch({ run, rowCount: 0 });
  if (outcome !== 'finished-empty') return LATEST_BATCH_WAITING_COPY;
  return runTerminalExplanation(run) || LATEST_BATCH_EMPTY_FALLBACK;
}
